import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';

export interface Summary {
  id: string;
  title: string;
  content: string;
  created_at: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  user_id: string;
  process_id?: string;
  // Time range fields
  start_date?: string;
  end_date?: string;
  date_range_display?: string;
  date_range?: string;
  summary_type?: string;
  source?: string;
  visible_to_users?: boolean;
}

interface SummariesState {
  summaries: Summary[];
  weeklySummaries: Summary[];
  currentSummary: Summary | null;
  loading: boolean;
  weeklySummariesLoading: boolean;
  error: string | null;
  generating: boolean;
  triggering: boolean;
}

const initialState: SummariesState = {
  summaries: [],
  weeklySummaries: [],
  currentSummary: null,
  loading: false,
  weeklySummariesLoading: false,
  error: null,
  generating: false,
  triggering: false,
};

// Async thunks
export const generateSummary = createAsyncThunk(
  'summaries/generate',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.post(`${backendUrl}/api/summaries/generate`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to generate summary');
    }
  }
);

export const fetchSummaries = createAsyncThunk(
  'summaries/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string; user: { role: 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user' } } };
      // Use different endpoints based on user role
      // Admin roles (admin, anis-admin, superadmin) see all summaries
      // Regular users (user, anis-user) see only visible summaries
      const adminRoles = ['admin', 'anis-admin', 'superadmin'];
      const isAdmin = auth.user?.role ? adminRoles.includes(auth.user.role) : false;
      const endpoint = isAdmin ? '/api/summaries/' : '/api/summaries/visible';
      const response = await axios.get(`${backendUrl}${endpoint}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch summaries');
    }
  }
);

export const fetchSummary = createAsyncThunk(
  'summaries/fetchOne',
  async (id: string, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/summaries/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch summary');
    }
  }
);

export const deleteSummary = createAsyncThunk(
  'summaries/delete',
  async (id: string, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      await axios.delete(`${backendUrl}/api/summaries/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete summary');
    }
  }
);

export const renameSummary = createAsyncThunk(
  'summaries/rename',
  async (
    { id, title }: { id: string; title: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.patch(
        `${backendUrl}/api/summaries/${id}/title`,
        { title },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data as Summary;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to rename summary');
    }
  }
);

export const fetchWeeklySummaries = createAsyncThunk(
  'summaries/fetchWeekly',
  async (source: string | undefined, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const params = source ? `?source=${source}` : '';
      const response = await axios.get(`${backendUrl}/api/summaries/weekly${params}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch weekly summaries');
    }
  }
);

export const triggerWeeklySummary = createAsyncThunk(
  'summaries/triggerWeekly',
  async (
    { start_date, end_date, sources }: { start_date: string; end_date: string; sources: string[] },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.post(
        `${backendUrl}/api/summaries/weekly/trigger`,
        { start_date, end_date, sources },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to trigger weekly summary generation');
    }
  }
);

const summariesSlice = createSlice({
  name: 'summaries',
  initialState,
  reducers: {
    clearCurrentSummary: (state) => {
      state.currentSummary = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate Summary
      .addCase(generateSummary.pending, (state) => {
        state.generating = true;
        state.error = null;
      })
      .addCase(generateSummary.fulfilled, (state, action) => {
        state.generating = false;
        state.currentSummary = action.payload;
        state.summaries.unshift(action.payload);
      })
      .addCase(generateSummary.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload as string;
      })
      // Fetch Summaries
      .addCase(fetchSummaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSummaries.fulfilled, (state, action) => {
        state.loading = false;
        state.summaries = action.payload;
      })
      .addCase(fetchSummaries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Single Summary
      .addCase(fetchSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSummary = action.payload;
      })
      .addCase(fetchSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Summary
      .addCase(deleteSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summaries = state.summaries.filter(summary => summary.id !== action.payload);
        state.weeklySummaries = state.weeklySummaries.filter(summary => summary.id !== action.payload);
        if (state.currentSummary && state.currentSummary.id === action.payload) {
          state.currentSummary = null;
        }
      })
      .addCase(deleteSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Rename Summary
      .addCase(renameSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renameSummary.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.summaries = state.summaries.map((s) => (s.id === updated.id ? updated : s));
        state.weeklySummaries = state.weeklySummaries.map((s) => (s.id === updated.id ? updated : s));
        if (state.currentSummary?.id === updated.id) {
          state.currentSummary = updated;
        }
      })
      .addCase(renameSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Weekly Summaries
      .addCase(fetchWeeklySummaries.pending, (state) => {
        state.weeklySummariesLoading = true;
        state.error = null;
      })
      .addCase(fetchWeeklySummaries.fulfilled, (state, action) => {
        state.weeklySummariesLoading = false;
        state.weeklySummaries = action.payload;
      })
      .addCase(fetchWeeklySummaries.rejected, (state, action) => {
        state.weeklySummariesLoading = false;
        state.error = action.payload as string;
      })
      // Trigger Weekly Summary
      .addCase(triggerWeeklySummary.pending, (state) => {
        state.triggering = true;
        state.error = null;
      })
      .addCase(triggerWeeklySummary.fulfilled, (state) => {
        state.triggering = false;
      })
      .addCase(triggerWeeklySummary.rejected, (state, action) => {
        state.triggering = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentSummary, clearError } = summariesSlice.actions;
export default summariesSlice.reducer;
